/**
 * WebCrypto helpers for credential encryption at rest.
 *
 * Design:
 *   - Passphrase entered once per browser session, never persisted.
 *   - PBKDF2-SHA256, 600k iterations -> 256-bit key.
 *   - AES-GCM with a fresh random 96-bit IV per ciphertext.
 *   - Only the IndexedDB `credentials` store is encrypted; other stores
 *     hold low-value public metadata.
 *
 * `PassphraseSession` holds the derived key in module-scoped memory. Call
 * `clear()` on logout / tab hide if you want tighter security; otherwise the
 * key disappears when the tab closes.
 */

const PBKDF2_ITERATIONS = 600_000;
const KEY_LENGTH_BITS = 256;
const AES_NAME = 'AES-GCM';

export type Base64 = string;

function toBase64(buf: ArrayBuffer | Uint8Array): Base64 {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) {
    s += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(s);
}

/**
 * `Uint8Array<ArrayBuffer>` rather than the default
 * `Uint8Array<ArrayBufferLike>`: TypeScript 5.9's WebCrypto typings reject
 * `SharedArrayBuffer`-backed views, so we constrain the type up front.
 */
export type Bytes = Uint8Array<ArrayBuffer>;

/**
 * Allocate a `Uint8Array` whose backing buffer is a plain `ArrayBuffer`
 * (not `SharedArrayBuffer`). Runtime behavior is identical to
 * `new Uint8Array(n)`.
 */
function newBytes(length: number): Bytes {
  return new Uint8Array(new ArrayBuffer(length));
}

function fromBase64(b64: Base64): Bytes {
  const s = atob(b64);
  const out = newBytes(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export function randomSalt(): Bytes {
  return crypto.getRandomValues(newBytes(16));
}

export function randomIv(): Bytes {
  return crypto.getRandomValues(newBytes(12));
}

export async function deriveKey(passphrase: string, salt: Bytes): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: AES_NAME, length: KEY_LENGTH_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
}

export interface EncryptedBlob {
  ct: Base64;
  iv: Base64;
}

export async function encryptJson(plaintext: unknown, key: CryptoKey): Promise<EncryptedBlob> {
  const iv = randomIv();
  const enc = new TextEncoder();
  const data = enc.encode(JSON.stringify(plaintext));
  const ctBuf = await crypto.subtle.encrypt({ name: AES_NAME, iv }, key, data);
  return { ct: toBase64(ctBuf), iv: toBase64(iv) };
}

export async function decryptJson<T>(ct: Base64, iv: Base64, key: CryptoKey): Promise<T> {
  const ivBuf = fromBase64(iv);
  const ctBuf = fromBase64(ct);
  const ptBuf = await crypto.subtle.decrypt({ name: AES_NAME, iv: ivBuf }, key, ctBuf);
  const text = new TextDecoder().decode(ptBuf);
  return JSON.parse(text) as T;
}

/**
 * Module-scoped current derived key. Set once via {@link PassphraseSession.set},
 * cleared on tab close (or explicitly). Never exposes the raw passphrase.
 */
class PassphraseSessionImpl {
  private key: CryptoKey | undefined;
  private salt: Bytes | undefined;
  private accountIdentity: string | undefined;

  isUnlocked(): boolean {
    return this.key !== undefined;
  }

  isUnlockedFor(accountIdentity: string | undefined): boolean {
    return this.key !== undefined && this.accountIdentity === accountIdentity;
  }

  /** Returns the current key or throws — UI should check `isUnlocked` first. */
  requireKey(): CryptoKey {
    if (!this.key) {
      throw new Error('No passphrase set — UI should prompt before encrypted operations.');
    }
    return this.key;
  }

  getSalt(): Bytes | undefined {
    return this.salt;
  }

  setKey(key: CryptoKey, salt: Bytes, accountIdentity?: string): void {
    this.key = key;
    this.salt = salt;
    this.accountIdentity = accountIdentity;
  }

  async unlock(passphrase: string, salt: Bytes): Promise<void> {
    this.setKey(await deriveKey(passphrase, salt), salt);
  }

  clear(): void {
    this.key = undefined;
    this.salt = undefined;
    this.accountIdentity = undefined;
  }
}

export const PassphraseSession = new PassphraseSessionImpl();

export const cryptoUtils = { toBase64, fromBase64 };
